/* ------------------------------------------------------------------ */
/*  Session shim around window.fetch (AUTH-02)                         */
/*                                                                     */
/*  The API keeps the sign-in in an HttpOnly cookie (cortex_session)   */
/*  and refuses a write without "X-Requested-With: cortex" (CSRF). So  */
/*  every call to the API, wherever a screen makes it, goes out with   */
/*  credentials: "include" (dev's UI on :3000 calls :8000) and, for    */
/*  POST/PUT/PATCH/DELETE, that header. A 401 from the API means the   */
/*  session has ended: onSessionEnded's listeners hear of it (App      */
/*  signs out and shows the message). A 401 from users/login is a      */
/*  wrong password, not an ended session.                              */
/*                                                                     */
/*  index.js installs it before anything renders. Other URLs pass      */
/*  through untouched.                                                 */
/* ------------------------------------------------------------------ */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";
export const CSRF_HEADER = "X-Requested-With";
export const CSRF_VALUE = "cortex";
export const SESSION_ENDED = "Your session has ended. Sign in again.";
const WRITES = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const IDENTITY_HEADER = "X-Cortex-Identity";
const IDENTITY_MISMATCH = "mismatch";
const SESSION_KEY = "cortexqa-session"; // theme.js

const listeners = new Set();

/* Call `listener` when an API call answers 401; returns the unsubscribe. */
export function onSessionEnded(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function absolute(url) {
  try {
    return new URL(url, window.location.href).href;
  } catch {
    return "";
  }
}

/* Whether `url` is a call to the API: under REACT_APP_API_URL, or the
   same-origin /api/ that nginx proxies. */
export function isApiUrl(url) {
  const href = absolute(url);
  if (!href) return false;
  return href.startsWith(absolute(API_BASE_URL)) || href.startsWith(absolute("/api/"));
}

/* The request's headers as a plain object, from a Headers, an array of pairs
   or an object. */
function headersOf(headers) {
  const out = {};
  if (!headers) return out;
  if (Array.isArray(headers)) headers.forEach(([k, v]) => (out[k] = v));
  else if (typeof headers.forEach === "function") headers.forEach((v, k) => (out[k] = v));
  else Object.assign(out, headers);
  return out;
}

/* The init a call to the API goes out with. */
export function withSession(input, init = {}) {
  const fromRequest = typeof Request !== "undefined" && input instanceof Request;
  const method = String(init.method || (fromRequest ? input.method : "GET")).toUpperCase();
  const headers = headersOf(init.headers || (fromRequest ? input.headers : undefined));
  const named = Object.keys(headers).some((k) => k.toLowerCase() === CSRF_HEADER.toLowerCase());
  if (WRITES.has(method) && !named) headers[CSRF_HEADER] = CSRF_VALUE;
  return { ...init, credentials: "include", headers };
}

function urlOf(input) {
  if (typeof input === "string") return input;
  if (input && typeof input.url === "string") return input.url;
  return String(input?.href || input || "");
}

/* The API says this tab names another user: someone signed in as a different
   user in another tab, and the cookie is shared. Forget this tab's stored user
   and reload, so App asks GET users/me who is signed in. Once per page load. */
function restartAsSessionUser(win) {
  if (win.cortexRestarting) return;
  win.cortexRestarting = true;
  try {
    win.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* reload anyway */
  }
  win.location.reload();
}

export function installApiShim(win = window) {
  if (!win.fetch || win.fetch.cortexSession) return;
  const original = win.fetch.bind(win);
  const shimmed = async (input, init) => {
    const url = urlOf(input);
    if (!isApiUrl(url)) return original(input, init);
    const res = await original(input, withSession(input, init));
    if (res.status === 403 && res.headers?.get?.(IDENTITY_HEADER) === IDENTITY_MISMATCH) restartAsSessionUser(win);
    if (res.status === 401 && !/\/users\/login(?:[?#]|$)/.test(absolute(url))) {
      listeners.forEach((listener) => {
        try {
          listener();
        } catch {
          /* a listener's failure must not fail the call */
        }
      });
    }
    return res;
  };
  shimmed.cortexSession = true;
  win.fetch = shimmed;
}
